/* @path: /src/pages/shelbula/workspaces/ShelbulaWorkspaces.jsx */
// @version: 3.5 (Refactor CSS Cleanup)
// @description: Added support for REFACTOR_CSS_CLEANUP type in refactor handler.

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { authManager, TIERS } from '@/managers/AuthManager';
import { Code2, FileText, LayoutDashboard, Loader, Zap } from 'lucide-react';
import { AccountMenuButton } from '@/components/layout/HeaderActionButtons';
import WorkspaceSidebar from './WorkspaceSidebar';
import WorkspaceDetailPanel from './WorkspaceDetailPanel';
import { motion, AnimatePresence } from 'framer-motion';
import WorkspaceSettingsModal from './WorkspaceSettingsModal';
import ConfirmDeleteModal from './ConfirmationModals/ConfirmDeleteModal';
import ConfirmUndoModal from './ConfirmationModals/ConfirmUndoModal';
import ConfirmRevertToPointModal from './ConfirmationModals/ConfirmRevertToPointModal';
import ConfirmRollbackToPlanModal from './ConfirmationModals/ConfirmRollbackToPlanModal';
import ConfirmForcePullModal from './ConfirmationModals/ConfirmForcePullModal';
import { apiManager } from './apiManager';
import FileEditorPanel from './FileEditorPanel';
import TerminalPanel from './TerminalPanel';
import TerminalPanelV2 from './TerminalPanelV2';
import RenameFileModal from './RenameFileModal';
import ConfirmFileDeleteModal from './ConfirmationModals/ConfirmFileDeleteModal';
import CreatePathModal from './CreatePathModal';
import CreateWorkspaceModal from './CreateWorkspaceModal';
import ConfirmCancelModal from './ConfirmationModals/ConfirmCancelModal';
import ConfirmRestartModal from './ConfirmationModals/ConfirmRestartModal';
import DirectActionModal from './DirectActionModal';
import CloneRepoModal from './CloneRepoModal';
import CreateRepoModal from './ConfirmationModals/CreateRepoModal';
import ReportView from './ReportView';
import { PIN_TYPES, pinManager } from '@/components/tools/pinned/PinManager';
import { keyStateManager } from '@/managers/zKeysManager';
import { workspaceManager } from '@/managers/workspaceManager';
import { useConversation } from '@/managers/ConvoManager';
import { usePanelManager, RightSidebarViews, CONTENT_TYPES } from '@/managers/PanelManager';
import ProjectPortal from '@/components/layout/RightPanel/ProjectPortal';
import { canAccessWorkspaces } from '@/utils/workspaceAccess';
import { isTerminalV2Enabled, isWorkspaceBackendEnabled } from '@/utils/setProdOrDev';
import { areWorkspaceContextIdsEqual, isPrivateWorkspaceContextId, normalizeWorkspaceContextId } from '@/utils/workspaceContext';
import { getEffectiveActiveToolIds } from '@/utils/workspaceAccessSettings';

const WORKSPACE_LIMITS = {
  [TIERS.FREE]: 3,
  [TIERS.PLUS]: 3,
  [TIERS.PRO]: 3,
  [TIERS.EVOK]: 10,
  [TIERS.EVOK_ULTRA]: 100,
  [TIERS.BETA]: Infinity,
  [TIERS.ADMIN]: Infinity,
};

const WorkspaceEmptyState = () => (
    <div className="text-center p-8">
        <h2 className="text-xl font-bold text-on-surface">Workspaces</h2>
        <p className="text-on-surface-dim text-sm mt-2 max-w-xl mx-auto">The Main Workspace holds your shared ongoing context. Created workspaces isolate files, thread history, and repo state when a project needs its own boundary.</p>
        <div className="mt-8 space-y-4 max-w-lg mx-auto">
            
            <div className="bg-surface p-6 rounded-lg border border-border-color text-left flex flex-col transition-all hover:border-primary/50 hover:shadow-lg">
                <div className="flex justify-between items-start">
                    <h3 className="text-base font-bold text-primary">Main Workspace</h3>
                    <span className="flex-shrink-0 ml-4 bg-green-500/20 text-green-300 text-xs font-semibold px-2.5 py-1 rounded-full">
                        Shared by default
                    </span>
                </div>
                <p className="text-xs text-on-surface-dim mt-3 leading-relaxed flex-grow">
                    Use the main workspace when you want one unified memory and fast thread switching without pinning the conversation to a separate container.
                </p>
            </div>

            <div className="bg-surface p-6 rounded-lg border border-border-color text-left flex flex-col transition-all hover:border-primary/50 hover:shadow-lg">
                <div className="flex justify-between items-start">
                    <h3 className="text-base font-bold text-primary">Created Workspaces</h3>
                     <span className="flex-shrink-0 ml-4 bg-indigo-500/20 text-indigo-300 text-xs font-semibold px-2.5 py-1 rounded-full">
                        Isolated when needed
                    </span>
                </div>
                <p className="text-xs text-on-surface-dim mt-3 leading-relaxed flex-grow">
                   Keep the existing sidebar, file tree, repo sync, and analysis flow, but treat each created workspace as its own sealed working context.
                </p>
            </div>

        </div>
    </div>
);

const WorkspaceBackendDisabledState = () => (
    <div className="flex h-full w-full items-center justify-center bg-background-darker px-8 text-center">
        <div className="max-w-xl space-y-3">
            <h2 className="text-xl font-bold text-on-surface">Workspace backend is offline</h2>
            <p className="text-sm text-on-surface-dim">The workspace surface is still available for design and navigation work, but this build is not connected to the native workspace backend.</p>
        </div>
    </div>
);

const VIEW_TABS = [
    { id: RightSidebarViews.WORKSPACE, label: 'Workspace', icon: LayoutDashboard },
    { id: RightSidebarViews.CODE, label: 'Code', icon: Code2 },
    { id: RightSidebarViews.DOCUMENT, label: 'Document', icon: FileText },
];

const ShelbulaWorkspaces = ({ onLogout }) => {
    const { currentConversation, updateConversation } = useConversation();
    const { rightSidebarView, setRightSidebarView, clearPortalContent, getPortalContent } = usePanelManager();
    const [workspaceState, setWorkspaceState] = useState(workspaceManager.getState());
    
    const { 
        workspaces: allWorkspaces, 
        isLoading, 
        error, 
        activeWorkspaceId,
        activeWorkspaceDetail,
        isDetailLoading,
        detailError 
    } = workspaceState;
    
    const [authState, setAuthState] = useState(authManager.getState());
    const [pinState, setPinState] = useState(() => pinManager.getState());
    const [userTier, setUserTier] = useState(authManager.getTier());
    const [lastFileTreeUpdate, setLastFileTreeUpdate] = useState(null);
    const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });
    const [detailPanelTab, setDetailPanelTab] = useState('overview');
    const [currentFiles, setCurrentFiles] = useState([]);
    const [openFiles, setOpenFiles] = useState([]);
    const [activeFilePath, setActiveFilePath] = useState(null);
    const [isGithubPatReady, setIsGithubPatReady] = useState(false);
    const [isCreatingRepo, setIsCreatingRepo] = useState(false);
    const [isCloning, setIsCloning] = useState(false);
    const [isFetching, setIsFetching] = useState(false);
    const [dataRefreshTrigger, setDataRefreshTrigger] = useState(0);

    const [isSettingsModalOpen, setSettingsModalOpen] = useState(false);
    const [isConfirmDeleteModalOpen, setConfirmDeleteModalOpen] = useState(false);
    const [isConfirmUndoModalOpen, setConfirmUndoModalOpen] = useState(false);
    const [isConfirmRevertToPointModalOpen, setConfirmRevertToPointModalOpen] = useState(false);
    const [isConfirmRollbackToPlanModalOpen, setConfirmRollbackToPlanModalOpen] = useState(false);
    const [isConfirmRestartModalOpen, setConfirmRestartModalOpen] = useState(false);
    const [isRenameModalOpen, setRenameModalOpen] = useState(false);
    const [isConfirmFileDeleteModalOpen, setConfirmFileDeleteModalOpen] = useState(false);
    const [isCreatePathModalOpen, setCreatePathModalOpen] = useState(false);
    const [isCreateModalOpen, setCreateModalOpen] = useState(false);
    const [isConfirmCancelModalOpen, setConfirmCancelModalOpen] = useState(false);
    const [isDirectActionModalOpen, setDirectActionModalOpen] = useState(false);
    const [isCloneIntoModalOpen, setCloneIntoModalOpen] = useState(false);
    const [isTerminalOpen, setIsTerminalOpen] = useState(false);
    const [isCreateRepoModalOpen, setIsCreateRepoModalOpen] = useState(false);
    const [isReportViewOpen, setIsReportViewOpen] = useState(false);
    const [isConfirmForcePullModalOpen, setConfirmForcePullModalOpen] = useState(false);
    const [workspaceForModal, setWorkspaceForModal] = useState(null);
    const [taskForModal, setTaskForModal] = useState(null);
    const [commitForModal, setCommitForModal] = useState(null);
    const [nodeForModal, setNodeForModal] = useState(null);
    const [terminalInitialPath, setTerminalInitialPath] = useState(null);
    const [createPathModalType, setCreatePathModalType] = useState('file');
    const [isCreatingPath, setIsCreatingPath] = useState(false);

    const zipInputRef = useRef(null);

    const activeFile = useMemo(() => openFiles.find(f => f.path === activeFilePath), [openFiles, activeFilePath]);
    const pinnedFilePaths = useMemo(() => {
        const activePins = pinState.pins.filter((pin) => pinManager.isActive(pin.id));
        return new Set(
            activePins
                .filter((pin) => (
                    pin.type === PIN_TYPES.WORKSPACE_FILE
                    && pin.workspaceId === activeWorkspaceId
                    && (!pin.conversationId || pin.conversationId === currentConversation?.id)
                ))
                .map((pin) => pin.filePath)
        );
    }, [activeWorkspaceId, currentConversation?.id, pinState.activeConversationPins, pinState.pins]);
    const portalContent = getPortalContent();
    const canUseWorkspaces = useMemo(() => canAccessWorkspaces(authState.user), [authState.user]);
    const isWorkspaceBackendEnabledFlag = useMemo(() => isWorkspaceBackendEnabled(), []);
    const isTerminalV2EnabledFlag = useMemo(() => isTerminalV2Enabled(), []);
    const isWorkspaceExplorerOnlyMode = false;
    const canRunAutonomousTasks = useMemo(() => [TIERS.EVOK, TIERS.EVOK_ULTRA, TIERS.BETA, TIERS.ADMIN].includes(userTier), [userTier]);
    const { atLimit, limit } = useMemo(() => {
        const count = allWorkspaces.length;
        const currentLimit = WORKSPACE_LIMITS[userTier] ?? 0;
        return { atLimit: count >= currentLimit - 1, limit: currentLimit };
    }, [allWorkspaces, userTier]);
    
    const activeWorkspaceWithDetails = useMemo(() => {
        const selectedWorkspace = allWorkspaces.find(ws => ws.id === activeWorkspaceId);
        const mergedSelectedWorkspace = selectedWorkspace || null;
        
        if (activeWorkspaceDetail && activeWorkspaceDetail.id === activeWorkspaceId) {
            return activeWorkspaceDetail;
        }
        
        return mergedSelectedWorkspace || null;
    }, [activeWorkspaceDetail, allWorkspaces, activeWorkspaceId]);

    const resolvedActiveWorkspaceId = activeWorkspaceWithDetails?.id || activeWorkspaceId;
    
    const isBrainEnabled = useMemo(() => {
        const activeIds = getEffectiveActiveToolIds({
            conversation: currentConversation,
            workspace: activeWorkspaceWithDetails,
            fallbackActiveToolIds: ['Workspace_Brain'],
        });
        return activeIds.includes('Workspace_Brain');
    }, [currentConversation, activeWorkspaceWithDetails]);

    const hasPendingApprovalTask = useMemo(() => allWorkspaces.some(ws => ws.status === 'PENDING_APPROVAL' || ws.status === 'RE_PLANNING'), [allWorkspaces]);
    const hasOtherPendingActions = useMemo(() => allWorkspaces.some(ws => ws.id !== activeWorkspaceId && (ws.status === 'PENDING_APPROVAL' || ws.status === 'RE_PLANNING')), [activeWorkspaceId, allWorkspaces]);
    
    const setActiveWorkspaceId = useCallback((id) => {
        const nextWorkspaceId = normalizeWorkspaceContextId(id);
        workspaceManager.setActiveWorkspace(nextWorkspaceId);
        setCurrentFiles([]);
        setOpenFiles([]);
        setActiveFilePath(null);
        if (currentConversation?.id && !areWorkspaceContextIdsEqual(currentConversation.workspaceContextId, nextWorkspaceId)) {
            updateConversation(currentConversation.id, { workspaceContextId: nextWorkspaceId }).catch((error) => {
                console.error('[ShelbulaWorkspaces] Failed to persist selected workspace to conversation:', error);
            });
        }
    }, [currentConversation, updateConversation]);

    const handleDataRefresh = useCallback(() => {
        setDataRefreshTrigger(count => count + 1);
        workspaceManager.refreshNow?.().catch((error) => {
            console.error('[ShelbulaWorkspaces] Immediate workspace refresh failed:', error);
        });
    }, []);
    const showNotification = useCallback((message, type = 'success') => {
        setNotification({ show: true, message, type });
        setTimeout(() => setNotification({ show: false, message: '', type: 'success' }), 4500);
    }, []);
    
    const handleFullFileListLoad = useCallback((files) => {
        setCurrentFiles(files);
    }, []);

    useEffect(() => {
        const unsubAuth = authManager.subscribe((state) => { setAuthState(state); setUserTier(state.tier); });
        const unsubWs = workspaceManager.subscribe(setWorkspaceState);
        const unsubPins = pinManager.subscribe(setPinState);
        const handleKeys = (keyState) => setIsGithubPatReady(!!keyState.keys?.GITHUB);
        const unsubKeys = keyStateManager.subscribe(handleKeys);
    
        if (isWorkspaceBackendEnabledFlag) {
            workspaceManager.connectListStream();
        } else {
            workspaceManager.disconnect();
        }
        
        if (keyStateManager.getState().isKeysReady) handleKeys(keyStateManager.getState()); 
        else keyStateManager.refreshFromDB();
        
        const handleRefreshEvent = () => handleDataRefresh();
        window.addEventListener('workspace-refresh-required', handleRefreshEvent);

        return () => { 
            unsubAuth(); 
            unsubWs(); 
            unsubPins();
            unsubKeys();
            workspaceManager.disconnect();
            window.removeEventListener('workspace-refresh-required', handleRefreshEvent);
        };
    }, [isWorkspaceBackendEnabledFlag, handleDataRefresh]);

    if (!canUseWorkspaces) {
        return null;
    }

    if (!isWorkspaceBackendEnabledFlag) {
        return <WorkspaceBackendDisabledState />;
    }
    
    useEffect(() => {
        if (detailError) {
            showNotification(detailError, 'error');
        }
    }, [detailError, showNotification]);
    
    useEffect(() => {
        const newTimestamp = activeWorkspaceWithDetails?.lastFileTreeUpdate;
        if (newTimestamp && newTimestamp !== lastFileTreeUpdate) {
            showNotification('File tree synced with latest changes.', 'success');
            handleDataRefresh();
            setLastFileTreeUpdate(newTimestamp);
        }
    }, [activeWorkspaceWithDetails, lastFileTreeUpdate, handleDataRefresh, showNotification]);

    useEffect(() => {
        if (!currentConversation?.id) {
            return;
        }

        const conversationWorkspaceId = normalizeWorkspaceContextId(currentConversation.workspaceContextId);
        if (isPrivateWorkspaceContextId(conversationWorkspaceId)) {
            return;
        }
        if (!areWorkspaceContextIdsEqual(activeWorkspaceId, conversationWorkspaceId)) {
            workspaceManager.setActiveWorkspace(conversationWorkspaceId);
            setCurrentFiles([]);
            setOpenFiles([]);
            setActiveFilePath(null);
        }
    }, [activeWorkspaceId, currentConversation]);

    const handleInitiateCreate = () => {
        if (atLimit) { showNotification(`You have reached your limit of ${limit} workspace(s).`, 'error'); return; }
        setCreateModalOpen(true);
    };

    const handleWorkspaceCreated = useCallback((workspaceId) => {
        setRightSidebarView(RightSidebarViews.WORKSPACE);
        setDetailPanelTab('overview');
        setActiveWorkspaceId(workspaceId);
    }, [setRightSidebarView, setActiveWorkspaceId]);
    
    const handleConfirmDelete = async (workspaceId) => {
        try {
            const result = await apiManager.deleteWorkspace(workspaceId);
            showNotification(result?.reset ? 'Default workspace cleared and reset.' : 'Workspace deleted successfully.', 'success');
            if (activeWorkspaceId === workspaceId) setActiveWorkspaceId(null);
            workspaceManager.refreshNow?.().catch(() => {});
        } catch (err) { showNotification(`Error deleting: ${err.message}`, 'error'); } 
        finally { setConfirmDeleteModalOpen(false); setWorkspaceForModal(null); }
    };
    
    const handleConfirmCancel = async (workspaceId, phaseId) => {
        showNotification('Cancelling task...', 'info');
        try {
            await apiManager.cancelAndRevertPhase(workspaceId, phaseId);
            showNotification("Task cancelled and reverted.", 'success');
            handleDataRefresh();
        } catch (err) { showNotification(`Error cancelling: ${err.message}`, 'error'); } 
        finally { setConfirmCancelModalOpen(false); setWorkspaceForModal(null); setTaskForModal(null); }
    };
    
    const handleConfirmRestart = async () => {
        if (!workspaceForModal || !taskForModal) return;
        const phaseIdToRestart = taskForModal.phaseId || taskForModal.id;
        showNotification('Restarting task...', 'info');
        try {
            await apiManager.restartPhase(workspaceForModal.id, phaseIdToRestart);
            showNotification("Task is restarting.", 'success');
            handleDataRefresh();
        } catch (err) { showNotification(`Error restarting: ${err.message}`, 'error'); } 
        finally { setConfirmRestartModalOpen(false); setWorkspaceForModal(null); setTaskForModal(null); }
    };

    const handleConfirmUndo = async () => {
        if (!workspaceForModal || !taskForModal) return;
        const phaseIdToUndo = taskForModal.phaseId || taskForModal.id;
        showNotification('Undoing last phase...', 'info');
        try {
            await apiManager.undoPhase(workspaceForModal.id, phaseIdToUndo);
            showNotification("Phase successfully undone.", 'success');
            handleDataRefresh();
        } catch (err) { showNotification(`Error undoing phase: ${err.message}`, 'error'); } 
        finally { setConfirmUndoModalOpen(false); setWorkspaceForModal(null); setTaskForModal(null); }
    };
    
    const handleConfirmRevertToPoint = async () => {
        if (!workspaceForModal || !commitForModal || !taskForModal?.id) return;
        showNotification('Reverting to point...', 'info');
        try {
            await apiManager.revertToPoint(workspaceForModal.id, commitForModal, taskForModal.id);
            showNotification("Workspace reverted.", 'success');
            handleDataRefresh();
        } catch (err) { showNotification(`Error reverting: ${err.message}`, 'error'); }
        finally { setConfirmRevertToPointModalOpen(false); setWorkspaceForModal(null); setTaskForModal(null); setCommitForModal(null); }
    };
    
    const handleConfirmRollbackToPlan = async () => {
        if (!workspaceForModal?.id || !taskForModal?.id || !taskForModal?.planApprovalCommitHash) return;
        showNotification('Rolling back to plan...', 'info');
        try {
            await apiManager.revertToPoint(workspaceForModal.id, taskForModal.planApprovalCommitHash, taskForModal.id);
            showNotification("Workspace rolled back to plan stage.", 'success');
            handleDataRefresh();
        } catch (err) { showNotification(`Error rolling back: ${err.message}`, 'error'); }
        finally { setConfirmRollbackToPlanModalOpen(false); setWorkspaceForModal(null); setTaskForModal(null); }
    };

    const handleCreateRepo = useCallback(async ({ repoName, isPrivate }) => {
        if (!isGithubPatReady || !activeWorkspaceWithDetails) return;
        setIsCreatingRepo(true);
        showNotification(`Creating repository '${repoName}'...`, 'info');
        try {
            const result = await apiManager.createRepoFromWorkspace(activeWorkspaceWithDetails.id, repoName, isPrivate);
            showNotification(`Successfully linked repo: ${result.repoUrl}`, 'success');
            setIsCreateRepoModalOpen(false);
        } catch (err) { showNotification(`Error creating repository: ${err.message}`, 'error'); } 
        finally { setIsCreatingRepo(false); }
    }, [isGithubPatReady, activeWorkspaceWithDetails, showNotification]);

    const handleCloneIntoWorkspace = useCallback(async ({ repoUrl }) => {
        if (!activeWorkspaceId) return "No active workspace selected.";
        setIsCloning(true);
        showNotification('Cloning repository...', 'info');
        try {
            await apiManager.cloneIntoWorkspace(activeWorkspaceId, repoUrl);
            showNotification('Repository cloned successfully.', 'success');
            setCloneIntoModalOpen(false);
            handleDataRefresh();
            return null;
        } catch (err) {
            showNotification(`Error cloning: ${err.message}`, 'error');
            return err.message;
        } finally { setIsCloning(false); }
    }, [activeWorkspaceId, showNotification, handleDataRefresh]);

    const handleInitiateForcePull = useCallback(() => {
        if (!activeWorkspaceWithDetails) return;
        setWorkspaceForModal(activeWorkspaceWithDetails);
        setConfirmForcePullModalOpen(true);
    }, [activeWorkspaceWithDetails]);
    
    const handleConfirmForcePull = useCallback(async () => {
        if (!workspaceForModal?.id || isFetching) return;
        
        const workspaceId = workspaceForModal.id;
        setIsFetching(true);
        showNotification('Force pulling remote changes... This may take a moment.', 'info');
        
        try {
            await apiManager.hardPullFromRemote(workspaceId);
            showNotification('Workspace successfully synced with remote.', 'success');
            handleDataRefresh();
        } catch (err) {
            showNotification(`Error during force pull: ${err.message}`, 'error');
        } finally {
            setIsFetching(false);
            setConfirmForcePullModalOpen(false);
            setWorkspaceForModal(null);
        }
    }, [workspaceForModal, isFetching, showNotification, handleDataRefresh]);

    const handlePhaseInitiated = (message) => {
        showNotification(message, 'success');
        setDirectActionModalOpen(false);
    };

    const handleOpenFile = useCallback((node) => {
        clearPortalContent();
        setOpenFiles(current => current.some(f => f.path === node.path) ? current : [...current, { path: node.path, name: node.name }]);
        setActiveFilePath(node.path);
    }, [clearPortalContent]);
    const handleCloseFile = useCallback((filePath) => { setOpenFiles(current => { const newFiles = current.filter(f => f.path !== filePath); if (activeFilePath === filePath) setActiveFilePath(newFiles[newFiles.length - 1]?.path || null); return newFiles; }); }, [activeFilePath]);
    const handleCloseAllFiles = useCallback(() => {
        setOpenFiles([]);
        setActiveFilePath(null);
    }, []);
    
    const handleSaveFile = async (filePath, fileContent, commitMessage) => {
        if (!resolvedActiveWorkspaceId) return;
        showNotification(`Saving ${filePath}...`, 'info');
        try {
            await apiManager.saveFileContent(resolvedActiveWorkspaceId, filePath, fileContent, commitMessage);
            showNotification(`File saved.`, 'success');
        } catch(err) { showNotification(`Error saving: ${err.message}`, 'error'); throw err; }
    };

    const handleDropFiles = async (workspaceId, droppedFiles) => {
        showNotification(`Uploading ${droppedFiles.length} file(s)...`, 'info');
        try {
            const filesToUpload = await Promise.all(
                Array.from(droppedFiles).map(file => new Promise((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onload = (e) => resolve({ filePath: file.name, fileContent: e.target.result });
                    reader.onerror = reject;
                    reader.readAsText(file);
                }))
            );
            await apiManager.saveMultipleFiles(workspaceId, filesToUpload);
            showNotification(`${filesToUpload.length} file(s) added.`, 'success');
            handleDataRefresh();
        } catch (err) { showNotification(`Error uploading: ${err.message}`, 'error'); }
    };

    const handleConfirmRename = async (oldPath, newPath) => {
        if (!resolvedActiveWorkspaceId) return;
        showNotification(`Renaming...`, 'info');
        try {
            await apiManager.renameFile(resolvedActiveWorkspaceId, oldPath, newPath);
            const pinsToUpdate = pinState.pins.filter((pin) => (
                pin.type === PIN_TYPES.WORKSPACE_FILE
                && pin.workspaceId === resolvedActiveWorkspaceId
                && (pin.filePath === oldPath || pin.filePath.startsWith(`${oldPath}/`))
            ));
            await Promise.all(pinsToUpdate.map((pin) => {
                const nextPinnedPath = pin.filePath === oldPath
                    ? newPath
                    : `${newPath}${pin.filePath.slice(oldPath.length)}`;
                return pinManager.updatePin(pin.id, {
                    filePath: nextPinnedPath,
                    title: nextPinnedPath,
                });
            }));
            showNotification(`Renamed successfully.`, 'success');
            
            // Update open files
            setOpenFiles(prev => prev.map(f => f.path === oldPath ? { ...f, path: newPath, name: newPath.split('/').pop() } : f));
            
            // Update active file if needed
            if (activeFilePath === oldPath) setActiveFilePath(newPath);

            // Trigger file tree refresh
            handleDataRefresh(); 
        } catch (err) { showNotification(`Error renaming: ${err.message}`, 'error'); } 
        finally { setRenameModalOpen(false); }
    };

    const handleConfirmDeleteFile = async (node) => {
        if (!resolvedActiveWorkspaceId || !node) return;
        const { path: filePath, type } = node;

        showNotification(`Deleting ${filePath}...`, 'info');
        try {
            await apiManager.deleteFile(resolvedActiveWorkspaceId, filePath);
            const pinsToDelete = pinState.pins.filter((pin) => (
                pin.type === PIN_TYPES.WORKSPACE_FILE
                && pin.workspaceId === resolvedActiveWorkspaceId
                && (pin.filePath === filePath || pin.filePath.startsWith(`${filePath}/`))
            ));
            await Promise.all(pinsToDelete.map((pin) => pinManager.deletePin(pin.id)));
            showNotification(`Deleted ${filePath}.`, 'success');
            
            if (type === 'directory') {
                const prefix = filePath + '/';
                setCurrentFiles(prev => prev.filter(f => !f.path.startsWith(prefix)));
            } else {
                setCurrentFiles(prev => prev.filter(f => f.path !== filePath));
            }
            handleCloseFile(filePath);
            handleDataRefresh();
        } catch (err) {
            showNotification(`Error deleting: ${err.message}`, 'error');
            handleDataRefresh();
        } finally {
            setConfirmFileDeleteModalOpen(false);
        }
    };

    const handleZipFileSelected = useCallback(async (event) => {
        const file = event.target.files?.[0];
        if (!file || !resolvedActiveWorkspaceId) return;
        showNotification('Uploading and unzipping archive...', 'info');
        try {
            await apiManager.uploadAndUnzip(resolvedActiveWorkspaceId, file);
            showNotification('Archive processed successfully.', 'success');
            handleDataRefresh();
        } catch (err) { showNotification(`Error uploading zip: ${err.message}`, 'error'); } 
        finally { if (event.target) event.target.value = null; }
    }, [resolvedActiveWorkspaceId, showNotification, handleDataRefresh]);

    const handleToggleFilePin = useCallback(async (node) => {
        if (!resolvedActiveWorkspaceId || !node || node.type !== 'file') return;

        const existingPin = pinState.pins.find((pin) => (
            pin.type === PIN_TYPES.WORKSPACE_FILE
            && pin.workspaceId === resolvedActiveWorkspaceId
            && pin.filePath === node.path
            && (!pin.conversationId || pin.conversationId === currentConversation?.id)
        ));

        if (existingPin && pinManager.isActive(existingPin.id)) {
            pinManager.deactivatePin(existingPin.id);
            showNotification(`Unpinned ${node.name} from every message.`, 'success');
            return;
        }

        if (existingPin) {
            pinManager.activatePin(existingPin.id);
            showNotification(`Pinned ${node.name} into every message.`, 'success');
            return;
        }

        try {
            const result = await apiManager.readFileContent(resolvedActiveWorkspaceId, node.path);
            const content = typeof result?.content === 'string' ? result.content : '';
            const newPin = await pinManager.createPin({
                title: node.path,
                type: PIN_TYPES.WORKSPACE_FILE,
                workspaceId: resolvedActiveWorkspaceId,
                conversationId: currentConversation?.id || null,
                filePath: node.path,
                content,
            });
            pinManager.activatePin(newPin.id);
            showNotification(`Pinned ${node.name} into every message.`, 'success');
        } catch (err) {
            showNotification(`Error pinning file: ${err.message}`, 'error');
        }
    }, [resolvedActiveWorkspaceId, currentConversation?.id, pinState.pins, showNotification]);

    const handleConfirmCreatePath = useCallback(async (pathValue) => {
        if (!pathValue || !resolvedActiveWorkspaceId) return;

        setIsCreatingPath(true);
        try {
            if (createPathModalType === 'folder') {
                await apiManager.createDirectory(resolvedActiveWorkspaceId, pathValue);
                showNotification('Folder created successfully.', 'success');
            } else {
                await apiManager.createFile(resolvedActiveWorkspaceId, pathValue);
                showNotification('File created successfully.', 'success');
            }
            setCreatePathModalOpen(false);
            handleDataRefresh();
        } catch (err) {
            showNotification(`Error creating ${createPathModalType}: ${err.message}`, 'error');
        } finally {
            setIsCreatingPath(false);
        }
    }, [resolvedActiveWorkspaceId, createPathModalType, showNotification, handleDataRefresh]);

    const handleInitiateCreateFile = useCallback(() => {
        setCreatePathModalType('file');
        setCreatePathModalOpen(true);
    }, []);

    const handleInitiateCreateFolder = useCallback(() => {
        setCreatePathModalType('folder');
        setCreatePathModalOpen(true);
    }, []);

    const handleDownloadZip = useCallback(async (workspace) => {
        if (!workspace) return;
        showNotification(`Preparing download for ${workspace.workspaceName}...`, 'info');
        try {
            const result = await apiManager.downloadWorkspaceZip(workspace.id);
            const { downloadUrl, downloadFileName } = result;

            if (!downloadUrl) {
                throw new Error("Server did not provide a download URL.");
            }

            const a = document.createElement('a');
            a.href = downloadUrl;
            a.download = downloadFileName || `${workspace.workspaceName || workspace.id}.zip`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(downloadUrl);
            showNotification('Download started.', 'success');
        } catch (err) {
            showNotification(`Error downloading zip: ${err.message}`, 'error');
        }
    }, [showNotification]);

    const handleDownloadFile = useCallback(async (node) => {
        if (!resolvedActiveWorkspaceId || !node || node.type !== 'file') return;
        showNotification(`Downloading ${node.name}...`, 'info');
        try {
            await apiManager.downloadFile(resolvedActiveWorkspaceId, node.path, node.name);
        } catch (err) {
            showNotification(`Error downloading file: ${err.message}`, 'error');
        }
    }, [resolvedActiveWorkspaceId, showNotification]);

    // Refactor handler updated to use direct sync endpoint with dynamic success message and type
    const handleInitiateRefactor = async (node, refactorType = 'SPLIT') => {
        if (!resolvedActiveWorkspaceId || !node) return;
        const typeLabel = refactorType === 'REFACTOR_CSS_CLEANUP' ? 'Cleanup' : 'Split';
        showNotification(`Refactoring (${typeLabel}) ${node.name}...`, 'info');
        try {
            const result = await apiManager.initiateRefactor(resolvedActiveWorkspaceId, node.path, refactorType);
            showNotification(`Refactor complete. ${result.filesCreated ? result.filesCreated + ' files updated.' : 'Success.'}`, 'success');
            handleDataRefresh(); 
        } catch (err) {
            showNotification(`Error refactoring: ${err.message}`, 'error');
        }
    };

    const showDetailLoading = isDetailLoading && !activeWorkspaceWithDetails;
    const hasCodePortalContent = portalContent?.type === CONTENT_TYPES.CODE || portalContent?.type === CONTENT_TYPES.MERMAID;

    const renderWorkspaceView = () => (
        <AnimatePresence mode="wait">
            {activeWorkspaceWithDetails ? (
                <motion.div key={activeWorkspaceId} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }} className="h-full">
                    <WorkspaceDetailPanel isWorkspaceExplorerOnlyMode={isWorkspaceExplorerOnlyMode} workspace={activeWorkspaceWithDetails} onInitiateUndo={(ws, task) => { setWorkspaceForModal(ws); setTaskForModal(task); setConfirmUndoModalOpen(true); }} onInitiateCancel={(ws, task) => { setWorkspaceForModal(ws); setTaskForModal(task); setConfirmCancelModalOpen(true); }} onInitiateRestart={(ws, task) => { setWorkspaceForModal(ws); setTaskForModal(task); setConfirmRestartModalOpen(true); }} onInitiateRevertToPoint={(ws, task, commit) => { setWorkspaceForModal(ws); setTaskForModal(task); setCommitForModal(commit); setConfirmRevertToPointModalOpen(true); }} onInitiateRollbackToPlan={(ws, task) => { setWorkspaceForModal(ws); setTaskForModal(task); setConfirmRollbackToPlanModalOpen(true); }} onOpenFile={handleOpenFile} onDownloadFile={handleDownloadFile} onInitiateRename={(node) => { setNodeForModal(node); setRenameModalOpen(true); }} onInitiateDeleteFile={(node) => { setNodeForModal(node); setConfirmFileDeleteModalOpen(true); }} onCopyForVSCode={() => {}} onDropFiles={handleDropFiles} onUploadZip={() => zipInputRef.current?.click()} onOpenTerminal={(node) => { setTerminalInitialPath(node?.type === 'directory' ? node.path : null); setIsTerminalOpen(true); }} showNotification={showNotification} onDataRefresh={handleDataRefresh} dataRefreshTrigger={dataRefreshTrigger} onFullFileListLoad={handleFullFileListLoad} activeTab={detailPanelTab} setActiveTab={setDetailPanelTab} onInitiateCreateFile={handleInitiateCreateFile} onInitiateCreateFolder={handleInitiateCreateFolder} onInitiateRefactor={handleInitiateRefactor} activeFilePath={activeFilePath} pinnedFilePaths={pinnedFilePaths} onToggleFilePin={handleToggleFilePin} isBrainEnabled={isBrainEnabled} />
                </motion.div>
            ) : (
                <div className="h-full flex items-center justify-center bg-background-darker">
                    {(isLoading || showDetailLoading || allWorkspaces.length > 0) ? (
                        <div className="flex items-center gap-3 text-on-surface-dim">
                            <Loader className="animate-spin text-primary" />
                            <p className="text-sm">Loading workspace...</p>
                        </div>
                    ) : (
                        <WorkspaceEmptyState />
                    )}
                </div>
            )}
        </AnimatePresence>
    );

    const renderCodeView = () => {
        return <ProjectPortal embedded forcedMode={CONTENT_TYPES.CODE} hideCloseButton isOpen={true} workspaceId={resolvedActiveWorkspaceId} />;
    };

    const renderDocumentView = () => <ProjectPortal embedded forcedMode={CONTENT_TYPES.DOCUMENT} hideCloseButton isOpen={true} workspaceId={resolvedActiveWorkspaceId} />;
    
    return (
        <div className="flex h-full w-full bg-background-dark text-on-surface relative overflow-hidden">
            <input type="file" ref={zipInputRef} onChange={handleZipFileSelected} style={{ display: 'none' }} accept=".zip"/>
            <div className="w-[210px] flex-shrink-0 border-r border-border-color h-full flex flex-col bg-background-dark">
                <WorkspaceSidebar 
                    workspaces={allWorkspaces} 
                    activeWorkspaceId={activeWorkspaceId}
                    activeWorkspace={activeWorkspaceWithDetails} 
                    onSelect={setActiveWorkspaceId} 
                    hasOtherPendingActions={hasOtherPendingActions} 
                    hasPendingApprovalTask={hasPendingApprovalTask} 
                    onOpenSettings={(ws) => { setWorkspaceForModal(ws); setSettingsModalOpen(true); }} 
                    onInitiateDelete={(ws) => { setWorkspaceForModal(ws); setConfirmDeleteModalOpen(true); }}
                    onDownloadZip={handleDownloadZip} 
                    isLoading={isLoading} 
                    error={error} 
                    isGithubPatReady={isGithubPatReady} 
                    onInitiateCreate={handleInitiateCreate} 
                    onInitiateClone={() => setCloneIntoModalOpen(true)} 
                    isCloning={isCloning} 
                    isCreatingRepo={isCreatingRepo} 
                    notification={notification} 
                    onInitiateForcePull={handleInitiateForcePull}
                    isFetching={isFetching}
                    createdCount={allWorkspaces.length}
                    workspaceLimit={limit}
                />
            </div>
            <main className="flex-1 flex flex-col overflow-hidden relative">
                <div className="h-[50px] border-b border-border-color bg-surface/80 backdrop-blur-sm px-4 flex items-center justify-between gap-3 shrink-0">
                    <div className="flex items-center gap-1.5">
                        {VIEW_TABS.map((tab) => (
                            <button
                                key={tab.id}
                                type="button"
                                onClick={() => setRightSidebarView(tab.id)}
                                aria-label={tab.label}
                                title={tab.label}
                                className={`inline-flex h-7 w-7 items-center justify-center self-center rounded-md border transition-colors duration-150 ${rightSidebarView === tab.id ? 'text-primary' : 'text-on-surface-dim hover:text-on-surface hover:bg-background-dark/70'}`}
                                style={rightSidebarView === tab.id
                                    ? { borderColor: 'rgba(var(--border-color-rgb), 0.65)', backgroundColor: 'rgba(var(--primary-rgb), 0.06)' }
                                    : { borderColor: 'transparent' }}
                            >
                                <tab.icon className="h-3.5 w-3.5" />
                            </button>
                        ))}
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                        <AccountMenuButton onLogout={onLogout} compact />
                    </div>
                </div>

                <div className="flex-1 min-h-0 relative overflow-hidden">
                    {rightSidebarView === RightSidebarViews.WORKSPACE && renderWorkspaceView()}
                    {rightSidebarView === RightSidebarViews.CODE && renderCodeView()}
                    {rightSidebarView === RightSidebarViews.DOCUMENT && renderDocumentView()}
                </div>

                {/* HIDDEN: Legacy delegate-task FAB — preserved for future reintegration.
                {rightSidebarView === RightSidebarViews.WORKSPACE && (
                    <button onClick={() => setDirectActionModalOpen(true)} className="absolute bottom-6 right-6 bg-primary hover:bg-primary-dark text-white rounded-full p-3 shadow-lg z-10 disabled:opacity-50 disabled:cursor-not-allowed" title={canRunAutonomousTasks ? "Start a new task" : "Autonomous tasks are available on higher tiers."} disabled={!canRunAutonomousTasks}>
                        <Zap size={20} />
                    </button>
                )}
                */}
            </main>
            <ConfirmForcePullModal isOpen={isConfirmForcePullModalOpen} onClose={() => setConfirmForcePullModalOpen(false)} onConfirm={handleConfirmForcePull} workspaceName={workspaceForModal?.workspaceName} isFetching={isFetching} />
            <CreateWorkspaceModal isOpen={isCreateModalOpen} onClose={() => setCreateModalOpen(false)} isGithubPatReady={isGithubPatReady} setActiveWorkspaceId={setActiveWorkspaceId} onWorkspaceCreated={handleWorkspaceCreated} showNotification={showNotification} />
            <DirectActionModal isOpen={isDirectActionModalOpen} onClose={() => setDirectActionModalOpen(false)} activeWorkspace={activeWorkspaceWithDetails} allWorkspaces={allWorkspaces} onPhaseInitiated={handlePhaseInitiated} onPhaseError={(msg) => showNotification(msg, 'error')} atLimit={atLimit} userTier={userTier} />
            <CloneRepoModal isOpen={isCloneIntoModalOpen} onClose={() => setCloneIntoModalOpen(false)} onConfirm={handleCloneIntoWorkspace} isCloning={isCloning} isGithubPatReady={isGithubPatReady} workspaceId={activeWorkspaceId} />
            <CreateRepoModal isOpen={isCreateRepoModalOpen} onClose={() => setIsCreateRepoModalOpen(false)} onConfirm={handleCreateRepo} isCreatingRepo={isCreatingRepo} defaultRepoName={activeWorkspaceWithDetails?.workspaceName.toLowerCase().replace(/\s+/g, '-')} />
            {isTerminalV2EnabledFlag ? (
                <TerminalPanelV2
                    isOpen={isTerminalOpen}
                    onClose={() => setIsTerminalOpen(false)}
                    workspaceId={activeWorkspaceId}
                    initialPath={terminalInitialPath}
                    onWorkspaceChanged={handleDataRefresh}
                />
            ) : (
                <TerminalPanel isOpen={isTerminalOpen} onClose={() => setIsTerminalOpen(false)} workspaceId={activeWorkspaceId} initialPath={terminalInitialPath} />
            )}
            <WorkspaceSettingsModal isOpen={isSettingsModalOpen} onClose={() => setSettingsModalOpen(false)} workspace={workspaceForModal} onInitiateDelete={() => { setSettingsModalOpen(false); setConfirmDeleteModalOpen(true); }} onUpdateSuccess={() => showNotification('Details updated.', 'success')} />
            <ConfirmDeleteModal isOpen={isConfirmDeleteModalOpen} onClose={() => setConfirmDeleteModalOpen(false)} onConfirm={handleConfirmDelete} workspace={workspaceForModal} />
            <ConfirmUndoModal isOpen={isConfirmUndoModalOpen} onClose={() => setConfirmUndoModalOpen(false)} onConfirm={handleConfirmUndo} task={taskForModal} />
            <ConfirmRevertToPointModal isOpen={isConfirmRevertToPointModalOpen} onClose={() => setConfirmRevertToPointModalOpen(false)} onConfirm={handleConfirmRevertToPoint} task={taskForModal} />
            <ConfirmRollbackToPlanModal isOpen={isConfirmRollbackToPlanModalOpen} onClose={() => setConfirmRollbackToPlanModalOpen(false)} onConfirm={handleConfirmRollbackToPlan} task={taskForModal} />
            <ConfirmCancelModal isOpen={isConfirmCancelModalOpen} onClose={() => setConfirmCancelModalOpen(false)} onConfirm={() => handleConfirmCancel(workspaceForModal?.id, taskForModal?.id)} workspace={workspaceForModal} task={taskForModal} />
            <ConfirmRestartModal isOpen={isConfirmRestartModalOpen} onClose={() => setConfirmRestartModalOpen(false)} onConfirm={handleConfirmRestart} workspace={workspaceForModal} task={taskForModal} />
            <RenameFileModal isOpen={isRenameModalOpen} onClose={() => setRenameModalOpen(false)} onConfirm={handleConfirmRename} fileNode={nodeForModal} />
            <ConfirmFileDeleteModal isOpen={isConfirmFileDeleteModalOpen} onClose={() => setConfirmFileDeleteModalOpen(false)} onConfirm={handleConfirmDeleteFile} fileNode={nodeForModal} />
            <CreatePathModal isOpen={isCreatePathModalOpen} onClose={() => setCreatePathModalOpen(false)} onConfirm={handleConfirmCreatePath} itemType={createPathModalType} isSubmitting={isCreatingPath} />
            <ReportView isOpen={isReportViewOpen} onClose={() => setIsReportViewOpen(false)} workspace={activeWorkspaceWithDetails} />
            <FileEditorPanel openFiles={openFiles} activeFile={activeFile} onSave={handleSaveFile} onCloseFile={handleCloseFile} onSetActiveFile={(path) => setActiveFilePath(path)} onCloseAll={handleCloseAllFiles} workspaceId={activeWorkspaceId} allFiles={currentFiles} onOpenFile={handleOpenFile} refreshToken={dataRefreshTrigger} />
        </div>
    );
};

export default ShelbulaWorkspaces;